import React, { useEffect, useMemo, useState } from "react";
import { Trash2, Plus, Copy, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Input } from "../ui/input";

const pill = (on) => `rounded-full px-3.5 py-1.5 text-[13px] transition-colors ${on ? "bg-[#183C35] text-[#F7F3EA]" : "bg-white border border-[#183C35]/15 text-[#183C35] hover:bg-[#F6F2E9]"}`;
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

// Real income only: pay-the-driver transfers + online payments that went through.
const incomeOf = (b) => {
  if (b.type === "hourly") return null;
  if (b.pay === "driver") return "driver";
  return b.payment_status === "paid" ? "online" : null;
};

const PERIODS = [["today", "Today"], ["month", "This month"], ["year", "This year"], ["all", "All time"]];
const inPeriod = (date, p) => {
  const t = today();
  if (p === "today") return date === t;
  if (p === "month") return (date || "").slice(0, 7) === t.slice(0, 7);
  if (p === "year") return (date || "").slice(0, 4) === t.slice(0, 4);
  return true;
};

const Stat = ({ label, value, sub, testid, dark }) => (
  <div className={`rounded-2xl p-4 sm:p-5 ${dark ? "bg-[#183C35] text-[#F7F3EA]" : "bg-white border border-[#183C35]/10"}`} data-testid={testid}>
    <p className={`text-[12px] uppercase tracking-[0.08em] ${dark ? "text-[#7FB8C4]" : "text-[#3E7F8E]"}`}>{label}</p>
    <p className="font-serif-d text-[26px] sm:text-[30px] leading-tight mt-1">€{value.toLocaleString("en", { maximumFractionDigits: 2 })}</p>
    <p className={`text-[12.5px] ${dark ? "text-[#F7F3EA]/70" : "text-[#202522]/55"}`}>{sub}</p>
  </div>
);

export const MoneySummary = ({ bookings }) => {
  const [period, setPeriod] = useState("month");
  const s = useMemo(() => {
    const r = { driver: 0, online: 0, nd: 0, no: 0 };
    bookings.filter((b) => inPeriod(b.date, period)).forEach((b) => {
      const k = incomeOf(b);
      if (k === "driver") { r.driver += Number(b.total) || 0; r.nd += 1; }
      if (k === "online") { r.online += Number(b.total) || 0; r.no += 1; }
    });
    return r;
  }, [bookings, period]);
  return (
    <div className="mb-8" data-testid="money-summary">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {PERIODS.map(([k, l]) => <button key={k} onClick={() => setPeriod(k)} className={pill(period === k)} data-testid={`period-${k}`}>{l}</button>)}
        <span className="text-[12px] text-[#202522]/50 ml-1">by ride date · unpaid card bookings not counted</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat dark label="Total income" value={s.driver + s.online} sub={`${s.nd + s.no} transfers`} testid="stat-total" />
        <Stat label="Pay the driver" value={s.driver} sub={`${s.nd} transfers`} testid="stat-driver" />
        <Stat label="Paid online" value={s.online} sub={`${s.no} transfers`} testid="stat-online" />
      </div>
    </div>
  );
};

const PAY_FILTERS = [["all", "All"], ["driver", "Pay driver"], ["paid", "Paid online"], ["unpaid", "Unpaid online"]];

export const useBookingFilter = (bookings) => {
  const [q, setQ] = useState("");
  const [date, setDate] = useState("");
  const [pay, setPay] = useState("all");
  const list = useMemo(() => bookings.filter((b) => {
    const text = `${b.ref} ${b.name} ${b.email} ${b.phone} ${b.pickup} ${b.dropoff || ""}`.toLowerCase();
    if (q && !text.includes(q.toLowerCase())) return false;
    if (date && b.date !== date) return false;
    if (pay === "driver" && b.pay !== "driver") return false;
    if (pay === "paid" && (b.pay === "driver" || b.payment_status !== "paid")) return false;
    if (pay === "unpaid" && (b.pay === "driver" || b.payment_status === "paid")) return false;
    return true;
  }), [bookings, q, date, pay]);
  const ui = (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-4" data-testid="booking-filters">
      <div className="relative sm:w-72">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#202522]/40" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, ref…" className="h-10 pl-9 rounded-full bg-white text-[16px] sm:text-[14px]" data-testid="booking-search" />
      </div>
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 rounded-full border border-[#183C35]/15 bg-white px-4 text-[16px] sm:text-[14px] text-[#202522]" data-testid="booking-date-filter" />
      <div className="flex flex-wrap gap-2">
        {PAY_FILTERS.map(([k, l]) => <button key={k} onClick={() => setPay(k)} className={pill(pay === k)} data-testid={`pay-filter-${k}`}>{l}</button>)}
      </div>
      {(q || date || pay !== "all") && <button onClick={() => { setQ(""); setDate(""); setPay("all"); }} className="text-[13px] underline text-[#3E7F8E]" data-testid="clear-filters">Clear</button>}
    </div>
  );
  return [list, ui];
};

const describe = (p) => p.fixed_total != null ? `Total €${p.fixed_total}` : p.amount_off != null ? `€${p.amount_off} off` : `${p.percent}% off`;

export const PromoCodesPanel = () => {
  const [codes, setCodes] = useState([]);
  const [kind, setKind] = useState("percent");
  const [value, setValue] = useState("10");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/promo-codes").then((r) => setCodes(r.data)).catch(() => toast.error("Could not load codes"));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/promo-codes", { kind, value: Number(value), note });
      setCodes((c) => [data, ...c]); setNote("");
      navigator.clipboard?.writeText(data.code).catch(() => {});
      toast.success(`Code ${data.code} created and copied`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create code"); }
    finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete code ${c.code}?`)) return;
    try { await api.delete(`/admin/promo-codes/${c.code}`); setCodes((l) => l.filter((x) => x.code !== c.code)); toast("Code deleted"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not delete code"); }
  };
  return (
    <div className="space-y-5">
      <form onSubmit={create} className="bg-white rounded-2xl border border-[#183C35]/10 p-4 sm:p-6" data-testid="promo-create-form">
        <p className="font-serif-d text-[20px] text-[#183C35]">Create a one-time code</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {[["percent", "% off"], ["amount", "€ off"], ["fixed", "Fixed total €"]].map(([k, l]) => (
            <button type="button" key={k} onClick={() => { setKind(k); setValue(k === "percent" ? "10" : k === "amount" ? "10" : "1"); }} className={pill(kind === k)} data-testid={`promo-kind-${k}`}>{l}</button>
          ))}
        </div>
        <div className="mt-3 grid sm:grid-cols-[140px_1fr_auto] gap-3">
          <Input type="number" min="1" step="any" value={value} onChange={(e) => setValue(e.target.value)} className="h-11 rounded-xl text-[16px] sm:text-[14.5px]" data-testid="promo-value" />
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional), e.g. for Maria" className="h-11 rounded-xl text-[16px] sm:text-[14.5px]" data-testid="promo-note" />
          <button disabled={busy} className="h-11 inline-flex items-center justify-center gap-2 rounded-full bg-[#183C35] text-[#F7F3EA] px-6 font-medium hover:bg-[#21524A] disabled:opacity-60 transition-colors" data-testid="promo-create-btn"><Plus className="w-4 h-4" />{busy ? "Creating…" : "Create code"}</button>
        </div>
        <p className="mt-2 text-[12px] text-[#202522]/50">Each code works for one booking only. % codes keep the €36 lowest fare per ride; € off never goes below €1.</p>
      </form>
      <div className="space-y-2" data-testid="promo-list">
        {codes.length === 0 ? <p className="py-6 text-center text-[#202522]/55">No codes yet.</p> : codes.map((c) => (
          <div key={c.code} className="bg-white rounded-xl border border-[#183C35]/10 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[14px]" data-testid={`promo-row-${c.code}`}>
            <button onClick={() => { navigator.clipboard?.writeText(c.code); toast("Copied"); }} className="font-mono font-semibold text-[#183C35] inline-flex items-center gap-1.5" title="Copy"><Copy className="w-3.5 h-3.5 text-[#3E7F8E]" />{c.code}</button>
            <span>{describe(c)}</span>
            <span className={`text-[12px] px-2 py-0.5 rounded-full font-medium ${c.used ? "bg-[#EEE] text-[#666]" : "bg-[#E3F4EA] text-[#1C7A43]"}`} data-testid={`promo-status-${c.code}`}>{c.used ? `Used${c.used_by ? ` · ${c.used_by}` : ""}` : "Unused"}</span>
            <span className="text-[12.5px] text-[#202522]/50">{c.note || (c.source_ref === "admin" ? "" : c.source_ref === "owner-test" ? "owner test" : "review email")}</span>
            {!c.used && <button onClick={() => remove(c)} className="ml-auto p-2 -m-1 text-[#B42318]/80 hover:text-[#B42318]" data-testid={`promo-delete-${c.code}`} aria-label="Delete code"><Trash2 className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
    </div>
  );
};

export const DeleteBookingButton = ({ b, onDeleted }) => (
  <button onClick={async () => {
    if (!window.confirm(`Delete booking ${b.ref} (${b.name})? This cannot be undone.`)) return;
    try { await api.delete(`/bookings/${b.ref}`); onDeleted(b.ref); toast(`Booking ${b.ref} deleted`); }
    catch { toast.error("Could not delete booking"); }
  }} className="mt-2 inline-flex items-center gap-1 text-[12px] text-[#B42318]/80 hover:text-[#B42318]" data-testid={`delete-booking-${b.ref}`}>
    <Trash2 className="w-3.5 h-3.5" />Delete
  </button>
);
